import React, { useRef } from "react";
import { Button } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { IActionHandler } from "@preveil-api";
import {
  FileUploadTypes, MessageAnchors, GlobalErrorMessages, Message, MessageHandlerDisplayType, AdminErrorMessages, useCsvDataValidation, AppConfiguration, PublicRoutes, QueryParamKeys,
  QueryParamValues, StatusCode
} from "src/common";
import { DeveloperWarningMessages, DeveloperSuccessMessages, DeveloperMessageAnchors, DeveloperErrorMessages, DeveloperTemplates, useCreateAccount } from "src/common/developers";
import { Icon, DraggableContainer } from "src/components";
import { CSVUserDisplay } from ".";
import { uiActions } from "src/store";

export type UserEntity = {
  email_address: string;
  display_name: string;
}

function CSVUserFormComponent() {
  const csv_column_names = ["User Address", "Display Name"]; // column name that matches the document template provided to the user.
  const dispatch = useDispatch();
  const csv_data_ref = useRef<Map<string, UserEntity>>();
  const { valid_addresses, invalid_addresses, duplicate_addresses, is_data_drop, handleValidateData, resetData } = useCsvDataValidation();
  const { createUser, success, setSuccess } = useCreateAccount();

  const CreateUsersRequests = {
    handleCsvData: (csv_file: any) => {
      handleCsvFileData(csv_file);
    },
    handleReset: () => {
      resetData();
    },
    handlePageError: (params: { message: string, stack?: any }) => {
      const { message, stack } = params;
      dispatch(uiActions.handleRequestErrors(new Message(message, MessageHandlerDisplayType.toastr), stack));
    }
  };

  function handlePageActions(action_obj: IActionHandler) {
    const callback = `handle${action_obj.actionType}`;
    if ((CreateUsersRequests as any)[callback] instanceof Function) {
      (CreateUsersRequests as any)[callback](action_obj.params);
    } else {
      const message = GlobalErrorMessages.no_handler_found.replace(MessageAnchors.actionType, action_obj.actionType);
      CreateUsersRequests.handlePageError({ message, stack: action_obj });
    }
  }

  function handleError(error: any, user_number: number, user_id: string) {
    switch (error.status) {
      case StatusCode.FORBIDDEN_DOMAIN:
        dispatch(uiActions.handleRequestErrors(new Message(DeveloperErrorMessages.error_forbidden, MessageHandlerDisplayType.toastr)));
        break;
      case StatusCode.NO_EMAIL_SIGNATURE_CODE:
        dispatch(uiActions.handleRequestErrors(new Message(DeveloperErrorMessages.error_API_error, MessageHandlerDisplayType.toastr)));
        break;
      case StatusCode.CONFLICT_CODE:
        dispatch(uiActions.handleRequestErrors(new Message(DeveloperErrorMessages.error_user_already_exists_multiple.replace(DeveloperMessageAnchors.user_id, user_id || "").replace(DeveloperMessageAnchors.user_count, user_number?.toString() || ""), MessageHandlerDisplayType.toastr)));
        break;
      default:
        dispatch(uiActions.handleRequestErrors(new Message(DeveloperErrorMessages.error_creating_multiple_users.replace(DeveloperMessageAnchors.user_count, user_number?.toString() || ""), MessageHandlerDisplayType.toastr)));
    }
  }

  async function handleCSVSubmit() {
    const is_web = AppConfiguration.buildForWeb();
    if (is_web) {
      dispatch(uiActions.handleRequestErrors(new Message(DeveloperWarningMessages.warning_wrong_port, MessageHandlerDisplayType.toastr)));
      return;
    }
    let success = true;
    for (let i = 0; i < valid_addresses.length; i++) {
      const user_id = valid_addresses[i];
      const display_name = csv_data_ref.current?.get(user_id)?.display_name || "Testing User";
      try {
        await createUser(user_id, display_name);
      } catch (error: any) {
        handleError(error, i, user_id);
        success = false;
        break;
      }
    }
    if (success) {
      setSuccess(true);
      dispatch(uiActions.handleSetMessage(new Message(DeveloperSuccessMessages.success_creating_multiple_users.replace(DeveloperMessageAnchors.user_count, valid_addresses.length.toString()))));
    }
  }

  async function handleCsvFileData(csv_file: any) {
    if (csv_file?.errors && csv_file?.errors.length >= 1) {
      // Papaparse returns some errors when the CSV formatting is not correct
      // or the data is missing a delimiter in each row, but that doesn't mean the data will not be parse.
      // so we are just logging those errors.
      const message = AdminErrorMessages.error_csv_upload_format;
      dispatch(uiActions.handleRequestErrors(new Message(message, MessageHandlerDisplayType.logger), csv_file.errors));
    }

    const { data, meta } = csv_file;
    const { fields } = meta;
    const csv_columns = new Set(fields);
    // we need to verify if the columns name inside the csv file matches the ones in the csv template.
    const is_valid_columns = csv_columns.has(csv_column_names[0]) && csv_columns.has(csv_column_names[1]);

    if (data.length === 0 || !is_valid_columns) {
      if (data.length === 0) {
        dispatch(uiActions.handleRequestErrors(new Message(AdminErrorMessages.error_csv_empty_file, MessageHandlerDisplayType.toastr), data));
      } else if (!is_valid_columns) {
        dispatch(uiActions.handleRequestErrors(new Message(AdminErrorMessages.error_csv_wrong_heading, MessageHandlerDisplayType.toastr), meta.fields));
      }
    } else {
      // any repeated address/domain from the csv file will be removed (only within the document).
      const address_column = csv_column_names[0];
      const display_name_column = csv_column_names[1];
      const csv_data_map = new Map<string, UserEntity>(data.map((address: any) => {
        const key = address[address_column];
        const email_address = address[address_column];
        const display_name = address[display_name_column] || email_address;
        return [key, { email_address, display_name }];
      }));
      csv_data_ref.current = csv_data_map; // Keeping a map of all the aliases (address, name) in a ref to persist between states, so we can use as a reference when submitting the data.
      const addresses_from_file = Array.from(csv_data_map.keys());
      handleValidateData(addresses_from_file);
    }
  };

  function handleDeleteAction(id: string | undefined) {
    if (!!id) {
      const data = [...valid_addresses, ...invalid_addresses, ...duplicate_addresses].filter((address: string) => address !== id);
      handleValidateData(data);
    }
  };

  return (
    <>
      {!is_data_drop ? <>
        <p>
          Download and use the upload template to accurately create multiple users at once
          <a
            href={DeveloperTemplates.developer_user_create}
            target="_blank"
            rel="noreferrer">
            <Icon className="ficon-download small" />
            Preveil-Developer-User-Create-Template.csv
          </a>
        </p>
        <DraggableContainer handleAction={handlePageActions} file_upload_type={FileUploadTypes.CSV} />
      </> : <>
        <CSVUserDisplay
          users={csv_data_ref.current || new Map<string, UserEntity>()}
          validate_email={valid_addresses}
          deleteFunction={handleDeleteAction}
        />
        <div className="btn-panel mt-4">
          <Button onClick={handleCSVSubmit}>Create Users</Button>
          <Button variant="no-outline-primary" onClick={() => resetData()}>Reset</Button>
          {success && <Button variant="link" href={`/${PublicRoutes.choose_account_route}?${QueryParamKeys.PAGE_ACTION_QUERY_KEY}=${QueryParamValues.switch}`}>Choose Account</Button>}
        </div>
      </>
      }
    </>
  );
}

export default React.memo(CSVUserFormComponent); 
